import React, { useEffect, useState } from 'react'

import { submitSessionRequest } from 'src/consumer/services/sessionRequestService'
import {
  IconArrowRight,
  IconCheck,
  IconClose,
  IconSparkle,
  IconWhatsapp,
} from './icons'

/*
 * RequestSessionModal
 *
 * A three-step curated flow that frames a mentor booking as a
 * "request" reviewed by the AUMHUM care team — deliberately not a
 * self-serve booking. The experience communicates scarcity and
 * clinical oversight rather than transactional availability.
 *
 *   1. Intro       – scarcity message, what happens next
 *   2. Form        – contact, preferred times, reason, urgency
 *   3. Success     – confirmation that the care team will reach out
 *                    on WhatsApp; support-side reminders are handled
 *                    server-side via SQS.
 */

const URGENCY_OPTIONS = [
  { id: 'flexible', label: 'Flexible — within a week' },
  { id: 'this_week', label: 'This week' },
  { id: 'urgent', label: 'Urgent — within 48 hours' },
]

const TIME_WINDOWS = [
  { id: 'weekday_morning', label: 'Weekday mornings' },
  { id: 'weekday_evening', label: 'Weekday evenings' },
  { id: 'weekend_morning', label: 'Weekend mornings' },
  { id: 'weekend_evening', label: 'Weekend evenings' },
]

const SESSION_TYPES = [
  { id: 'therapy', label: 'Therapy / counselling' },
  { id: 'psychiatry', label: 'Psychiatry consult' },
  { id: 'followup', label: 'Follow-up' },
  { id: 'unsure', label: "I'm not sure yet" },
]

const initialForm = {
  fullName: '',
  mobile: '',
  email: '',
  urgency: 'this_week',
  sessionType: 'unsure',
  preferredWindows: [],
  reason: '',
  consent: true,
}

function toggleInList(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

// Keep mobile input permissive — the server normalises to E.164.
const MOBILE_RE = /^[0-9+\-\s()]{7,20}$/

export default function RequestSessionModal({ open, onClose, mentor }) {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [ticketId, setTicketId] = useState(null)

  // Close with Escape; restore body scroll when closed.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // Reset internal state whenever the modal is (re-)opened so the user
  // isn't dropped onto the success screen on a repeat open.
  useEffect(() => {
    if (open) {
      setStep(1)
      setForm(initialForm)
      setSubmitting(false)
      setErrorMsg('')
      setTicketId(null)
    }
  }, [open])

  if (!open) return null

  const mentorName = mentor?.name || `${mentor?.firstName || ''} ${mentor?.lastName || ''}`.trim() || 'this practitioner'

  const validateFormStep = () => {
    if (!form.fullName?.trim() || form.fullName.trim().length < 2) {
      return 'Please enter your full name.'
    }
    if (!MOBILE_RE.test((form.mobile || '').trim())) {
      return 'Please enter a valid mobile number (with country code).'
    }
    if (form.preferredWindows.length === 0) {
      return 'Pick at least one time window that works for you.'
    }
    if (!form.consent) {
      return "Please confirm you'd like us to contact you on WhatsApp."
    }
    return ''
  }

  const handleSubmit = async () => {
    const err = validateFormStep()
    if (err) {
      setErrorMsg(err)
      return
    }
    setErrorMsg('')
    setSubmitting(true)
    try {
      const response = await submitSessionRequest({
        mentorSlug: mentor?.slug,
        mentorId: mentor?.id,
        fullName: form.fullName.trim(),
        mobile: form.mobile.trim(),
        email: (form.email || '').trim(),
        urgency: form.urgency,
        sessionType: form.sessionType,
        preferredWindows: form.preferredWindows,
        reason: (form.reason || '').trim(),
      })
      if (response?.ok) {
        setTicketId(response?.ticketId || null)
        setStep(3)
      } else {
        setErrorMsg(
          response?.message ||
            "We couldn't submit your request right now. Please try again in a moment.",
        )
      }
    } catch (e) {
      setErrorMsg("We couldn't reach our servers — please check your connection and retry.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="ml-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Request a session with ${mentorName}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="ml-modal">
        <button
          type="button"
          className="ml-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IconClose />
        </button>

        <div className="ml-modal-header">
          <div className="ml-modal-eyebrow">
            {step === 3 ? 'Request submitted' : 'Curated booking'}
          </div>
          <h2 className="ml-modal-title">
            {step === 1 && `Request a session with ${mentorName}`}
            {step === 2 && "A few details so we can reach out"}
            {step === 3 && "We'll be in touch soon"}
          </h2>
          <p className="ml-modal-sub">
            {step === 1 &&
              `${mentorName} is in high demand. Every slot is reviewed by our care team so you get matched with the right time and the right clinician.`}
            {step === 2 &&
              "Our care coordinator will message you on WhatsApp to confirm the best slot and any clinical context."}
            {step === 3 &&
              `Your request is with our care team. You'll get a WhatsApp message from our coordinator shortly.`}
          </p>

          {step !== 3 && (
            <div className="ml-modal-steps" aria-hidden="true">
              <span className="ml-modal-step-dot" data-active="true" />
              <span className="ml-modal-step-dot" data-active={step >= 2 ? 'true' : 'false'} />
              <span className="ml-modal-step-dot" data-active="false" />
            </div>
          )}
        </div>

        {step === 1 && (
          <>
            <ul className="ml-success-list" style={{ marginTop: '1.25rem' }}>
              <li>
                <IconCheck />
                <span>
                  Share a quick outline of what you need — we review it with the clinical team.
                </span>
              </li>
              <li>
                <IconCheck />
                <span>
                  We'll WhatsApp you within working hours with a confirmed slot and pre-visit
                  details.
                </span>
              </li>
              <li>
                <IconCheck />
                <span>
                  If {mentorName} is fully booked, we'll match you with an equally qualified
                  clinician in our network — same standards, same continuity.
                </span>
              </li>
            </ul>

            <div className="ml-modal-footer">
              <button type="button" className="ml-btn-ghost" onClick={onClose}>
                Not now
              </button>
              <button
                type="button"
                className="ml-cta-primary"
                onClick={() => setStep(2)}
              >
                <IconSparkle /> Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}
          >
            <div className="ml-field-stack">
              <div>
                <label className="ml-field-label" htmlFor="ml-name">
                  Your full name
                </label>
                <input
                  id="ml-name"
                  className="ml-field-input"
                  type="text"
                  autoComplete="name"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="ml-field-label" htmlFor="ml-mobile">
                  Mobile (with country code)
                </label>
                <input
                  id="ml-mobile"
                  className="ml-field-input"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+91 98765 43210"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  required
                />
                <div className="ml-field-hint">
                  We'll use this to reach you on WhatsApp — no spam, ever.
                </div>
              </div>

              <div>
                <label className="ml-field-label" htmlFor="ml-email">
                  Email (optional)
                </label>
                <input
                  id="ml-email"
                  className="ml-field-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div>
                <label className="ml-field-label">What kind of session?</label>
                <div className="ml-chip-select" role="radiogroup">
                  {SESSION_TYPES.map((o) => (
                    <button
                      type="button"
                      key={o.id}
                      role="radio"
                      aria-checked={form.sessionType === o.id}
                      data-active={form.sessionType === o.id ? 'true' : 'false'}
                      className="ml-chip-option"
                      onClick={() => setForm({ ...form, sessionType: o.id })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="ml-field-label">Preferred time windows</label>
                <div className="ml-chip-select">
                  {TIME_WINDOWS.map((o) => (
                    <button
                      type="button"
                      key={o.id}
                      data-active={form.preferredWindows.includes(o.id) ? 'true' : 'false'}
                      className="ml-chip-option"
                      onClick={() =>
                        setForm({
                          ...form,
                          preferredWindows: toggleInList(form.preferredWindows, o.id),
                        })
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <div className="ml-field-hint">Pick all that work — we'll match one.</div>
              </div>

              <div>
                <label className="ml-field-label">How soon?</label>
                <div className="ml-chip-select" role="radiogroup">
                  {URGENCY_OPTIONS.map((o) => (
                    <button
                      type="button"
                      key={o.id}
                      role="radio"
                      aria-checked={form.urgency === o.id}
                      data-active={form.urgency === o.id ? 'true' : 'false'}
                      className="ml-chip-option"
                      onClick={() => setForm({ ...form, urgency: o.id })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="ml-field-label" htmlFor="ml-reason">
                  What brings you in? (optional)
                </label>
                <textarea
                  id="ml-reason"
                  className="ml-field-textarea"
                  placeholder="A sentence or two is plenty — this stays with the care team."
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  maxLength={800}
                />
              </div>

              <label
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  fontSize: '0.8125rem',
                  color: 'var(--slate-600)',
                  alignItems: 'flex-start',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  I'm OK with AUMHUM's care team contacting me on WhatsApp and email about
                  this request.
                </span>
              </label>
            </div>

            {errorMsg ? (
              <div className="ml-error" role="alert">
                {errorMsg}
              </div>
            ) : null}

            <div className="ml-modal-footer">
              <button
                type="button"
                className="ml-btn-ghost"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="submit"
                className="ml-cta-primary"
                disabled={submitting}
                aria-disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Send request'} <IconArrowRight />
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <>
            <div className="ml-success-icon">
              <IconCheck />
            </div>
            <p className="ml-modal-sub" style={{ marginTop: 0 }}>
              {ticketId ? (
                <>
                  Your request <strong>#{ticketId}</strong> is with our care team.
                </>
              ) : (
                <>Your request is with our care team.</>
              )}{' '}
              Here's what happens next:
            </p>

            <ul className="ml-success-list">
              <li>
                <IconWhatsapp />
                <span>
                  A care coordinator will WhatsApp you within working hours (9am–9pm IST).
                </span>
              </li>
              <li>
                <IconCheck />
                <span>
                  They'll confirm {mentorName}'s next available slot — or match you with an
                  equally qualified clinician.
                </span>
              </li>
              <li>
                <IconCheck />
                <span>
                  You can reply right on WhatsApp to adjust timing, ask questions, or add
                  context.
                </span>
              </li>
            </ul>

            <div className="ml-modal-footer">
              <button type="button" className="ml-cta-primary" onClick={onClose}>
                Got it
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
